import useOcrSearch from "../../custom-hooks/useOcrSearch";
import OcrSearchHighlights, { OcrSearchStatus } from "../OcrSearchHighlights";
import React, { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";

//-----------------Icons----------------------
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as Calender } from "../../assets/icons/calendar.svg";
import { ReactComponent as Compass } from "../../assets/icons/compass.svg";
import { ReactComponent as Multiple } from "../../assets/icons/multiple-documents.svg";
import { ReactComponent as Document } from "../../assets/icons/document.svg";
import { ReactComponent as Close } from "../../assets/icons/close.svg";

// -----------PDFViewer imports----------------
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import {
  convertArabicToNumber,
  convertToArabicDate,
  toArabicNumber,
} from "../../utils/arabicConversions";
import "../../styles-arabic/pdf-modal.rtl.css";

import { toast } from "react-toastify";

const normalizePdfUrl = (url) => {
  if (!url) return "";

  try {
    const parsedUrl = new URL(url);
    parsedUrl.pathname = parsedUrl.pathname.toLowerCase();
    return parsedUrl.toString();
  } catch {
    const [path, query] = url.split("?");
    return `${path.toLowerCase()}${query ? `?${query}` : ""}`;
  }
};

const withUppercaseFilename = (url) => {
  try {
    const parsedUrl = new URL(url);
    const segments = parsedUrl.pathname.split("/");
    const filename = segments.pop();
    const dotIdx = filename.lastIndexOf(".");
    const stem = dotIdx > -1 ? filename.slice(0, dotIdx) : filename;
    const ext = dotIdx > -1 ? filename.slice(dotIdx) : "";
    segments.push(`${stem.toUpperCase()}${ext.toLowerCase()}`);
    parsedUrl.pathname = segments.join("/");
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

const pdfUrlExists = async (url) => {
  try {
    const res = await fetch(url, { headers: { Range: "bytes=0-0" } });
    return res.ok || res.status === 206;
  } catch {
    return false;
  }
};

// S3 object keys are case-sensitive. The app's convention is a lowercase
// filename, but some legacy collections were uploaded with an all-caps key,
// so fall back to that variant if the lowercase URL 403s.
const resolveCaseInsensitivePdfUrl = async (url) => {
  if (!url) return url;
  if (await pdfUrlExists(url)) return url;
  const upperUrl = withUppercaseFilename(url);
  if (upperUrl !== url && (await pdfUrlExists(upperUrl))) return upperUrl;
  return url;
};

// pdf.js error messages (e.g. "Unexpected server response (403) while
// retrieving PDF \"<url>\"") embed the raw S3 URL. Replace it with just the
// bucket and filename so the signed path/query string isn't exposed.
const maskS3UrlInMessage = (message) => {
  if (!message) return message;
  return message.replace(/"(https?:\/\/[^"]*)"/i, (fullMatch, url) => {
    try {
      const parsedUrl = new URL(url);
      if (!/\.amazonaws\.com$/i.test(parsedUrl.hostname)) return fullMatch;
      const bucket = parsedUrl.hostname.replace(/\.amazonaws\.com$/i, "");
      const filename = decodeURIComponent(parsedUrl.pathname.split("/").pop() || "");
      return `bucket ${bucket} and file ${filename}`;
    } catch {
      return "the requested file";
    }
  });
};

const renderPdfLoadError = (error) => (
  <div className="rpv-core__doc-error">
    <div className="rpv-core__doc-error-text">
      {maskS3UrlInMessage(error?.message) || "Failed to load the document."}
    </div>
  </div>
);

const PdfViewerModalArabic = ({ show, handleClose, searchQuery, matchType, row }) => {

  //For toolbar with print and download enabled
  const defaultLayoutPluginInstance2 = defaultLayoutPlugin();

  //For toolbar with print and download disabled
  const toolbarPluginInstance = toolbarPlugin();
  const { renderDefaultToolbar, Toolbar } = toolbarPluginInstance;
  const transform = (slot) => ({
    ...slot,
    // Disabling print, download, and open file buttons
    Download: () => <></>,
    Print: () => <></>,
    Open: () => <></>,
  });
  const defaultLayoutPluginInstance1 = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => {
      return <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>;
    },
  });

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const [maxPage, setMaxPage] = useState(row?.number_of_pages || 0);
  const [page, setPage] = useState(row?.page_number || 0);
  const [pdfUrl, setPDFurl] = useState(row?.AWS_link || "");
  const ocrSearch = useOcrSearch(pdfUrl, searchQuery, matchType, show);
  const pdfResolveTokenRef = useRef(0);

  const applyResolvedPdfUrl = async (candidateUrl) => {
    const token = ++pdfResolveTokenRef.current;
    const resolved = await resolveCaseInsensitivePdfUrl(candidateUrl);
    if (pdfResolveTokenRef.current === token) {
      setPDFurl(resolved);
    }
  };

  //Page number feature
  const [enteredPageNumber, setEnteredPageNumber] = useState("");

  const handlePageNumberChange = (e) => {
    const inputValue = e.target.value;
    setEnteredPageNumber(inputValue);
  };

  const navigateToEnteredPage = () => {
    const pageNumber = convertArabicToNumber(enteredPageNumber);
    if (pageNumber >= 1 && pageNumber <= maxPage) {
      setPage((prevPage) => {
        applyResolvedPdfUrl(get_new_file(pageNumber));
        return pageNumber;
      });
    }
  };

  const viewerPluginInstances = row?.is_download_blocked
    ? [
      defaultLayoutPluginInstance1,
      pageNavigationPluginInstance,
    ]
    : [
      defaultLayoutPluginInstance2,
      pageNavigationPluginInstance,
    ];

  useEffect(() => {
    if (pdfUrl) {
      viewerPluginInstances.forEach((plugin) => {
        if (plugin && typeof plugin.setCurrentPage === "function") {
          plugin.setCurrentPage(enteredPageNumber);
        }
      });
    }
  }, [pdfUrl, enteredPageNumber]);

  useEffect(() => {
    if (row) {
      setMaxPage(row.number_of_pages);
      setPage(row.page_number || 1);
      applyResolvedPdfUrl(normalizePdfUrl(row.AWS_link));
    }
  }, [row]);

  const {
    coll_name_arabic,
    date,
    date_of_journal,
    coll_location_arabic,
    coll_source_arabic,
    coll_type,
    date_of_publication,
    issue_number,
    volume,
    AWS_link,
    authors_arabic,
    title_arabic,
    place_of_publication_arabic,
    publisher_arabic,
  } = row || {};
  let fixedDate

  // Copy citation

  const [copySuccess, setCopySuccess] = useState("");

  let citationText;

  if (coll_type === "periodical") {
    fixedDate = date || date_of_journal;
    if (fixedDate) fixedDate = convertToArabicDate(fixedDate);
    citationText = `${coll_name_arabic}، المجلد ${volume}، العدد ${issue_number}، ${fixedDate}، مركز الخيرالله لدراسات الانتشار اللبناني arabicsearch.org، ${coll_source_arabic}، ${pdfUrl}`;
  } else {
    fixedDate = date || date_of_publication;
    if (fixedDate) fixedDate = convertToArabicDate(fixedDate);
    // First name Last name, Title, (Place of Publication: Publisher, Year), Khayrallah Center for Lebanese Diaspora Studies arabicsearch.org.

    // TODO - srane
    // Reformat data from Elastic & Postgres to be same format when passed to this componend (Currently Elastic->JsonArray, Postgres->String)
    let authorsList = authors_arabic
    if (!(typeof (authors_arabic) == 'string')) {
      authorsList = authors_arabic?.map(obj => obj.author_arabic) || []
    }
    citationText = `\u202B${authorsList}، ${title_arabic}، (${place_of_publication_arabic}: ${publisher_arabic}، ${fixedDate})، Khayrallah Center for Lebanese Diaspora Studies arabicsearch.org، ${pdfUrl}\u202C`;
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(citationText);
      setCopySuccess("تم النسخ!");
      setTimeout(() => setCopySuccess(""), 2000);
    } catch (err) {
      setCopySuccess("فشل النسخ");
    }
  };

  const get_new_file = (currentPage) => {
    const prefix = pdfUrl.split("_").slice(0, -1).join("_");
    return normalizePdfUrl(`${prefix}_${currentPage}.pdf`);
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage((prevPage) => {
        const newPage = prevPage - 1;
        applyResolvedPdfUrl(get_new_file(newPage));
        return newPage;
      });
    }
  };

  const handleNextPage = () => {
    if (page < maxPage) {
      setPage((prevPage) => {
        const newPage = prevPage + 1;
        applyResolvedPdfUrl(get_new_file(newPage));
        return newPage;
      });
    }
  };

  const handleEnterKey = (e) => {
    if (e.key === "Enter") {
      navigateToEnteredPage();
    }
  };

  //Todo Zoom debounce

  const isPreviousButtonDisabled = page === 1;
  const isNextButtonDisabled = page === maxPage;

  const handleCopyButtonClick = () => {
    navigator.clipboard.writeText(pdfUrl);
    toast.success("تم نسخ الرابط إلى الحافظة!", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
    });
  };

  return (
    <Modal show={show} onHide={handleClose} dialogClassName="my-modal">
      <Modal.Body closeButton style={{ justifyContent: "space-between" }}>
        <OcrSearchStatus search={ocrSearch} query={searchQuery} mode={matchType} arabic />
        <div className="container mt-3">
          <div className="row">
            <div className="col ColStyle" style={{ justifyContent: "end" }}>
              <Close onClick={handleClose} className="CloseBtnStyle" />
            </div>
            <div className="col-lg-auto ColStyle">
              <div className="CollNameStyle">{coll_name_arabic}</div>
            </div>
            <div className="col-md-auto ColStyle">
              <Calender className="IconStyle" />
              <div className="DataStyle">{fixedDate}</div>
            </div>
            <div className="col-md-auto ColStyle">
              <Compass className="IconStyle" />
              <div className="DataStyle">{coll_location_arabic}</div>
            </div>
            <div className="col-md-auto ColStyle">
              <Multiple className="IconStyle" />
              <div className="DataStyle">{coll_source_arabic}</div>
            </div>

            <div
              className="col-sm-auto ColStyle"
              style={{ justifyContent: "end" }}
            >
              <Button
                disabled={isNextButtonDisabled}
                onClick={handleNextPage}
                className="ButtonStyle"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                <span style={{ paddingLeft: "7px" }}>التالي</span>
              </Button>
            </div>
            <div
              className="col-sm-auto ColStyle"
              style={{ justifyContent: "center" }}
            >
              <Document className="IconStyle" />
              <div className="DataStyle">
                الصفحة {toArabicNumber(page.toString())} من{" "}
                {toArabicNumber(maxPage.toString())}
              </div>
            </div>
            <div
              className="col-sm-auto ColStyle"
              style={{ justifyContent: "start" }}
            >
              <Button
                disabled={isPreviousButtonDisabled}
                onClick={handlePreviousPage}
                className="ButtonStyle"
              >
                <span style={{ paddingRight: "7px" }}>السابق</span>
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
            <div
              className="col-sm-auto ColStyle"
              style={{ justifyContent: "end" }}
            >
              <Button
                onClick={handleCopyButtonClick}
                className="CopyButtonStyle"
              >
                <span style={{ paddingRight: "5px" }}> نسخ الرابط</span>
              </Button>
            </div>
            <div className="col-md-auto ColStyle">
              <label style={{ marginRight: "0.6rem" }}>
                الانتقال إلى الصفحة
              </label>
              <input
                type="text"
                value={enteredPageNumber}
                onChange={handlePageNumberChange}
                onKeyDown={handleEnterKey}
                className="PageNumberInput"
              />
            </div>
          </div>
          <div className="row">
            <strong>الاستشهاد:</strong>
            <span>
              {citationText}
              <Button
                style={{ marginLeft: "0.5rem" }}
                onClick={copyToClipboard}
                className="CopyButtonStyle"
              >
                <span style={{ paddingLeft: "5px" }}>نسخ الاستشهاد</span>
              </Button>
              {copySuccess && (
                <span style={{ color: "green", marginLeft: "10px" }}>
                  {copySuccess}
                </span>
              )}
            </span>
          </div>
        </div>

        {pdfUrl && (
          <div className="container mt-3">
            <Worker workerUrl={process.env.REACT_APP_PDF_WORKER}>
              <div>
                <Viewer
                  key={JSON.stringify([pdfUrl, searchQuery, matchType])}
                  theme="dark"
                  fileUrl={pdfUrl}
                  renderError={renderPdfLoadError}
                  renderPage={(props) => <div style={{ position: "relative", width: props.width, height: props.height }}>
                    {props.canvasLayer.children}
                    {props.textLayer.children}
                    {props.annotationLayer.children}
                    <OcrSearchHighlights search={ocrSearch} {...props} />
                  </div>}
                  plugins={
                    row?.is_download_blocked
                      ? [
                        defaultLayoutPluginInstance1,
                        pageNavigationPluginInstance,
                      ]
                      : [
                        defaultLayoutPluginInstance2,
                        pageNavigationPluginInstance,
                      ]
                  }
                />
              </div>
            </Worker>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PdfViewerModalArabic;
